var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { LoadingButton } from '@mui/lab';
import { Stack } from '@mui/material';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { StatusPermission } from '../../enum';
import { skipDisabledSteps } from '../../util';
import { useTerminologyTranslation } from '../app/terminology';
import { ConfirmDialog } from '../common';
import { WizardStep, getStepsLength, getWorkflowSteps } from './constants';
import { WizardContext } from './context';
import { useCompletedStep, useCurrentQueueCompletedStep, useQueuesAPI } from './queue/selectors';
import { useDisabledWizardSteps, useEffectiveCapacityPermission, usePlanTypeCategory, useWizardParams, } from './selectors';
import { WizardTopActionsSlotContext } from './topActionsSlotContext';
// Presentational NEXT/PREVIOUS pair. Rendered both at the bottom of the step and (via a portal)
// in the step header so long steps don't require scrolling to navigate.
var NavButtons = function (_a) {
    var previous = _a.previous, next = _a.next, previousLabel = _a.previousLabel, nextLabel = _a.nextLabel;
    return (_jsxs(Stack, __assign({ direction: "row", spacing: 4, justifyContent: "flex-end" }, { children: [previous.visible && (_jsx(LoadingButton, __assign({ loading: previous.loading, variant: "outlined", onClick: previous.onClick }, { children: previousLabel }))), next.visible && (_jsx(LoadingButton, __assign({ loading: next.loading, variant: "contained", onClick: next.onClick }, { children: nextLabel })))] })));
};
export var Actions = function () {
    var _a;
    var t = useTerminologyTranslation(['wizard', 'translation']).t;
    var capacityPermission = useEffectiveCapacityPermission();
    var _b = useWizardParams(), step = _b.step, queueId = _b.queueId;
    var planTypeCategory = usePlanTypeCategory();
    var stepsLength = getStepsLength(planTypeCategory);
    var workflowSteps = getWorkflowSteps(planTypeCategory);
    var disabledSteps = useDisabledWizardSteps();
    var currentStepIndex = workflowSteps.indexOf(step);
    var backStepIndex = skipDisabledSteps(workflowSteps, currentStepIndex - 1, disabledSteps, -1);
    var _c = useState(false), open = _c[0], setOpen = _c[1];
    var _d = useState(false), openDialog = _d[0], setOpenDialog = _d[1];
    var _e = useState(false), openAssignWarning = _e[0], setOpenAssignWarning = _e[1];
    var _f = useSearchParams(), searchParams = _f[0], setSearchParams = _f[1];
    var queuesData = useQueuesAPI()[0];
    var _g = useCompletedStep(), completedStep = _g[0], type = _g[1];
    var _h = useCurrentQueueCompletedStep(), currentQueueCompletedStep = _h[0], currentQueueIncompleteType = _h[1];
    var completedStepIndex = completedStep === -1 ? -1 : workflowSteps.indexOf(completedStep);
    var activeStepIndex = Math.min(currentStepIndex, completedStepIndex);
    var _j = useContext(WizardContext), handleSave = _j.handleSave, actionRef = _j.actionRef, requesting = _j.requesting, goToStep = _j.goToStep, isDirty = _j.methods.formState.isDirty;
    var topActionsSlot = useContext(WizardTopActionsSlotContext);
    var previousQueueIdRef = useRef(queueId);
    var previousStepIndexRef = useRef(currentStepIndex);
    // Use refs to store values so callbacks always read the latest value
    var currentQueueCompletedStepRef = useRef(currentQueueCompletedStep);
    var currentStepIndexRef = useRef(currentStepIndex);
    var completedStepRef = useRef(completedStep);
    var stepRef = useRef(step);
    // Update refs whenever the values change
    useEffect(function () {
        currentQueueCompletedStepRef.current = currentQueueCompletedStep;
    }, [currentQueueCompletedStep]);
    useEffect(function () {
        currentStepIndexRef.current = currentStepIndex;
    }, [currentStepIndex]);
    useEffect(function () {
        completedStepRef.current = completedStep;
    }, [completedStep]);
    useEffect(function () {
        stepRef.current = step;
    }, [step]);
    // Soft warning on the Queues step: flag queues/planning groups missing a line of business or language
    var hasUnassignedQueueFields = ((_a = queuesData.data) !== null && _a !== void 0 ? _a : []).some(function (q) { var _a; return !q.lobId || !((_a = q.languages) === null || _a === void 0 ? void 0 : _a.length); });
    var hasUnassignedRef = useRef(hasUnassignedQueueFields);
    useEffect(function () {
        hasUnassignedRef.current = hasUnassignedQueueFields;
    }, [hasUnassignedQueueFields]);
    var goToNextStep = useCallback(function () { return goToStep(skipDisabledSteps(workflowSteps, currentStepIndexRef.current + 1, disabledSteps)); }, [goToStep, workflowSteps, disabledSteps]);
    useEffect(function () {
        var queueChanged = previousQueueIdRef.current !== undefined && previousQueueIdRef.current !== queueId;
        // Don't adjust step automatically when switching queues - preserve the step the user was on
        // Only adjust if the queue hasn't changed (i.e., user is navigating steps, not switching queues)
        if (completedStep !== -1 && currentStepIndex !== activeStepIndex && !queueChanged) {
            searchParams.set('step', "".concat(activeStepIndex));
            setSearchParams(searchParams, { replace: true });
        }
        // Update refs for next render
        previousQueueIdRef.current = queueId;
        previousStepIndexRef.current = currentStepIndex;
    }, [activeStepIndex, completedStep, searchParams, setSearchParams, currentStepIndex, workflowSteps, queueId]);
    var doNext = useCallback(function () {
        // Read latest values from refs to ensure we have the most up-to-date status after save/refresh
        var latestCompletedStep = completedStepRef.current;
        var latestStep = stepRef.current;
        var latestCurrentStepIndex = currentStepIndexRef.current;
        // On the Queues step, surface a soft warning (continue / fix) when any queue or planning group
        // is missing its line of business or language, rather than hard-blocking.
        if (latestStep === WizardStep.Queues && hasUnassignedRef.current) {
            setOpenAssignWarning(true);
            return;
        }
        // Check if all queues are complete on the current step
        // If completedStep matches the current step, it means not all queues are complete on this step
        if (latestCompletedStep !== -1 && latestCompletedStep === latestStep) {
            setOpen(true);
            return;
        }
        // Also check current queue's completion status to ensure WAP and other required fields for the current queue block navigation
        var latestCurrentQueueCompletedStep = currentQueueCompletedStepRef.current;
        var currentQueueStepIndexForComparison = latestCurrentQueueCompletedStep === -1 ? -1 : workflowSteps.indexOf(latestCurrentQueueCompletedStep);
        // Block navigation if current queue's step is incomplete
        if (latestCurrentStepIndex === currentQueueStepIndexForComparison) {
            setOpen(true);
            return;
        }
        goToStep(skipDisabledSteps(workflowSteps, latestCurrentStepIndex + 1, disabledSteps));
    }, [goToStep, workflowSteps, disabledSteps]);
    var handleClick = function () {
        if (isDirty) {
            setOpenDialog(true);
            return;
        }
        handleSave('previous', function () { return goToStep(backStepIndex); })();
    };
    var handleConfirm = function () {
        setOpenDialog(false);
        handleSave('previous', function () { return goToStep(backStepIndex); })();
    };
    var previous = {
        visible: !!currentStepIndex && capacityPermission === StatusPermission.Edit,
        loading: actionRef.current === 'previous' && requesting,
        onClick: handleClick,
    };
    var next = {
        visible: currentStepIndex !== stepsLength - 1,
        loading: actionRef.current === 'next' && requesting,
        onClick: handleSave('next', doNext),
    };
    var navButtons = (_jsx(NavButtons, { previous: previous, next: next, previousLabel: t('translation:action.previous'), nextLabel: t('translation:action.next') }));
    return (_jsxs(_Fragment, { children: [navButtons, topActionsSlot && createPortal(navButtons, topActionsSlot), _jsx(ConfirmDialog, { open: open, onClose: function () { return setOpen(false); }, message: t("message.".concat(currentQueueIncompleteType || type, "Incomplete"), {
                    defaultValue: t('message.incomplete'),
                }), cancelButtonText: "ok" }), _jsx(ConfirmDialog, { open: openAssignWarning, onClose: function () { return setOpenAssignWarning(false); }, title: t('message.assignFieldsTitle'), message: t('message.assignFieldsWarning'), cancelButtonText: "cancel", confirmButtonText: "continue", onConfirm: function () {
                    setOpenAssignWarning(false);
                    goToNextStep();
                } }), isDirty && (_jsx(ConfirmDialog, { open: openDialog, onClose: function () { return setOpenDialog(false); }, title: t('translation:confirmation.unsavedChange'), message: t('translation:confirmation.confirmExit'), confirmButtonText: "saveContinue", skipSaveButtonText: "skipSave", onConfirm: handleConfirm, onSkipSaveConfirm: function () { return goToStep(backStepIndex); } }))] }));
};
